import { Icon, Text } from "@ttc3k/trekker";
import { Flex, styled } from "styled-system/jsx";
import { EyeSolid, Search } from "iconoir-react";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useImpersonation } from "shared/contextProviders/impersonation";
import { useImpersonationUserSearchLazyQuery } from "gql/generated";
import { IMPERSONATION_DEFAULTS } from "shared/constants";
import { WhereaboutsModal } from "shared/components";

type ImpersonationModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ImpersonationModal = ({
  isOpen,
  onClose,
}: ImpersonationModalProps) => {
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const [searchTerm, setSearchTerm] = useState("");
  const [executeSearch, { data, loading }] =
    useImpersonationUserSearchLazyQuery();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleImpersonate = useCallback(
    (userId: string, displayName: string) => {
      startImpersonation(userId, displayName);
      onClose();
      navigate("/app");
    },
    [startImpersonation, onClose, navigate],
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) return;
    debounceRef.current = setTimeout(() => {
      executeSearch({ variables: { search: value.trim() } });
    }, 300);
  };

  const results = data?.impersonationUserSearch ?? [];

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={onClose}
      HeaderIcon={EyeSolid}
      title="Impersonate User"
      description="View the app as another user. All actions will be performed with their permissions."
      size="sm"
      customFooter={<div />}
    >
      <Flex flexDir="column" gap="200">
        <Text visual="smallMedium" color="text.light">
          Quick Access
        </Text>
        <Flex flexDir="column" gap="75">
          <PickerItem
            onClick={() =>
              handleImpersonate(
                IMPERSONATION_DEFAULTS.DEV_PLAYGROUND_ADMIN.userId,
                IMPERSONATION_DEFAULTS.DEV_PLAYGROUND_ADMIN.label,
              )
            }
          >
            <Text visual="bodyMedium" color="text.mid">
              {IMPERSONATION_DEFAULTS.DEV_PLAYGROUND_ADMIN.label}
            </Text>
          </PickerItem>
          <PickerItem
            onClick={() =>
              handleImpersonate(
                IMPERSONATION_DEFAULTS.DEV_PLAYGROUND_OPERATOR.userId,
                IMPERSONATION_DEFAULTS.DEV_PLAYGROUND_OPERATOR.label,
              )
            }
          >
            <Text visual="bodyMedium" color="text.mid">
              {IMPERSONATION_DEFAULTS.DEV_PLAYGROUND_OPERATOR.label}
            </Text>
          </PickerItem>
          <PickerItem
            onClick={() =>
              handleImpersonate(
                IMPERSONATION_DEFAULTS.WHEREABOT.userId,
                IMPERSONATION_DEFAULTS.WHEREABOT.label,
              )
            }
          >
            <Text visual="bodyMedium" color="text.mid">
              {IMPERSONATION_DEFAULTS.WHEREABOT.label}
            </Text>
          </PickerItem>
        </Flex>

        <Text visual="smallMedium" color="text.light" mt="100">
          Search Users
        </Text>
        <Flex
          alignItems="center"
          gap="100"
          bg="white"
          borderRadius="100"
          px="150"
          py="100"
          border="1px solid"
          borderColor="border.light"
        >
          <Icon Element={Search} size="sm" color="icon.light" />
          <styled.input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            bg="transparent"
            border="none"
            outline="none"
            fontSize="sm"
            color="text.mid"
            w="full"
            _placeholder={{ color: "text.lightest" }}
          />
        </Flex>

        <Flex
          flexDir="column"
          gap="0"
          h="200px"
          overflowY="auto"
          bg="white"
          borderRadius="100"
          border="1px solid"
          borderColor="border.light"
        >
          {loading && (
            <Flex h="full" alignItems="center" justifyContent="center">
              <Text fontSize="xs" color="text.lightest">
                Searching...
              </Text>
            </Flex>
          )}

          {!loading &&
            searchTerm.trim().length >= 2 &&
            results.length > 0 &&
            results.map(user =>
              user ? (
                <PickerItem
                  key={user._id}
                  onClick={() =>
                    handleImpersonate(
                      user._id,
                      `${user.firstName} ${user.lastName}`,
                    )
                  }
                >
                  <Flex flexDir="column" gap="0">
                    <Text visual="bodyMedium" color="text.mid">
                      {user.firstName} {user.lastName}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="text.light"
                      lineClamp={1}
                      wordBreak="break-all"
                    >
                      {user.email}
                    </Text>
                  </Flex>
                </PickerItem>
              ) : null,
            )}

          {!loading && searchTerm.trim().length < 2 && (
            <Flex h="full" alignItems="center" justifyContent="center">
              <Text fontSize="xs" color="text.lightest">
                Type at least 2 characters to search
              </Text>
            </Flex>
          )}

          {!loading &&
            searchTerm.trim().length >= 2 &&
            results.length === 0 && (
              <Flex h="full" alignItems="center" justifyContent="center">
                <Text fontSize="xs" color="text.lightest">
                  No users found
                </Text>
              </Flex>
            )}
        </Flex>
      </Flex>
    </WhereaboutsModal>
  );
};

const PickerItem = styled("button", {
  base: {
    display: "flex",
    alignItems: "center",
    px: "150",
    py: "100",
    cursor: "pointer",
    bg: "white",
    border: "none",
    textAlign: "left",
    w: "full",
    borderRadius: "100",
    _hover: {
      bg: "bg.subtle",
    },
  },
});
